"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, ArrowRight, Truck, ShieldCheck, CircleCheck as CheckCircle2, Navigation, Clock, Building2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface RegionItem {
  id: string;
  name: string;
  hub: string;
  routes: string;
  focus: string;
}
interface FeatureItem {
  id: string;
  title: string;
  desc: string;
}
const regionsData: RegionItem[] = [{
  id: "berkshire",
  name: "Berkshire",
  hub: "Reading, Slough, Bracknell, Maidenhead, Newbury",
  routes: "M4 / A329(M) corridor",
  focus: "Commercial access, roofing contracts, and domestic builds"
}, {
  id: "surrey",
  name: "Surrey",
  hub: "Guildford, Woking, Epsom, Farnham, Camberley",
  routes: "M3 / M25 / A3 network",
  focus: "Residential developments, heritage properties, solar installs"
}, {
  id: "hampshire",
  name: "Hampshire",
  hub: "Basingstoke, Winchester, Andover, Farnborough, Aldershot",
  routes: "M3 / A303 / A31 links",
  focus: "Industrial units, housing schemes, commercial refurbishments"
}, {
  id: "buckinghamshire",
  name: "Buckinghamshire",
  hub: "High Wycombe, Aylesbury, Beaconsfield, Marlow",
  routes: "M40 / A404 corridor",
  focus: "Main contractor sites, roofing projects, temporary roofs"
}, {
  id: "london",
  name: "London & Greater London",
  hub: "West London, Heathrow Corridor, Richmond, Kingston",
  routes: "M4 / M25 / North & South Circular",
  focus: "Pavement licenses, confined access, high-spec commercial"
}];
const highlightsData: FeatureItem[] = [{
  id: "fast-survey",
  title: "Prompt site surveys",
  desc: "Direct visits from experienced estimators across all five counties."
}, {
  id: "nasc-standard",
  title: "NASC & CHAS accredited",
  desc: "Consistent safety audits and certified TG20:21 compliant erections."
}, {
  id: "transport-links",
  title: "Major artery access",
  desc: "Scheduled long-term programmes with responsive support when plans change."
}];
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="areas-we-cover" data-nav="light" className="relative bg-background py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-0.5 w-8 bg-primary block" />
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Operational Footprint
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight leading-tight">
            Scaffolding coverage across the South East
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Direct depot dispatch and experienced scaffold teams serving main
            contractors, developers, councils, and homeowners throughout the Home
            Counties.
          </p>
        </div>

        {/* Main Grid: Regional Breakdown & Visual Map Graphic */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Regional List */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 space-y-4">
            {regionsData.map((region, i) => <motion.div key={region.id} variants={itemVariants} data-index={i}>
                <Card className="rounded-lg border border-border bg-card shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] duration-200">
                  <CardContent className="p-5 sm:p-6">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2.5">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                          <MapPin className="size-4" />
                        </div>
                        <h3 className="text-lg font-bold font-default text-card-foreground">
                          {region.name}
                        </h3>
                      </div>
                      <span className="hidden sm:inline-flex items-center rounded-sm bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                        {region.routes}
                      </span>
                    </div>

                    <div className="space-y-1.5 mt-3 text-sm">
                      <p className="text-foreground">
                        <span className="font-semibold text-muted-foreground">
                          Key hubs:{" "}
                        </span>
                        {region.hub}
                      </p>
                      <p className="text-muted-foreground text-xs sm:text-sm">
                        <span className="font-medium text-muted-foreground">
                          Typical work:{" "}
                        </span>
                        {region.focus}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}

            {/* Coverage Action Bar */}
            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button asChild size="lg" className="font-semibold shadow-xs">
                <Link to="/AreasWeCoverPage">
                  <span>View full coverage details</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-medium">
                <Link to="/ContactRequestQuotePage">
                  <span>Check your project postcode</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Transport & Logistics Schematic Panel */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 space-y-6">
            {/* Structural Logistics Visual Graphic */}
            <div className="rounded-lg border border-border bg-card p-6 shadow-xs relative overflow-hidden">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-5">
                <div className="flex items-center gap-2">
                  <Navigation className="size-5 text-primary" />
                  <h4 className="text-base font-bold font-default text-card-foreground">
                    Corridor & Hub Schematic
                  </h4>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-2.5 py-0.5 text-xs font-semibold text-foreground">
                  <span className="size-2 rounded-full bg-primary" />
                  Active Fleet
                </span>
              </div>

              {/* Graphic Vector Map Simulation */}
              <div className="relative aspect-[4/3] rounded-md bg-muted/50 border border-border p-4 flex flex-col justify-between overflow-hidden">
                {/* Background Grid Lines */}
                <div className="absolute inset-0 bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:16px_16px] opacity-70 pointer-events-none" />

                {/* Corridor Lines (SVGs) */}
                <svg className="absolute inset-0 size-full pointer-events-none stroke-border" strokeWidth="2" strokeDasharray="4 4" fill="none">
                  <line x1="20%" y1="30%" x2="80%" y2="40%" />
                  <line x1="50%" y1="15%" x2="50%" y2="85%" />
                  <line x1="25%" y1="75%" x2="75%" y2="25%" />
                </svg>

                {/* North Tag */}
                <div className="flex justify-between items-start z-10 text-xs font-mono text-muted-foreground">
                  <span className="bg-background/80 px-2 py-0.5 rounded border border-border">
                    BUCKINGHAMSHIRE (M40)
                  </span>
                  <span className="bg-background/80 px-2 py-0.5 rounded border border-border">
                    GREATER LONDON (M25)
                  </span>
                </div>

                {/* Central Hub Marker */}
                <div className="z-10 my-auto self-center text-center bg-card border-2 border-primary rounded-lg p-3 shadow-md max-w-[200px]">
                  <div className="flex items-center justify-center gap-1.5 text-primary mb-1">
                    <Truck className="size-4" />
                    <span className="text-xs font-bold uppercase tracking-wide">
                      Core Depot Hub
                    </span>
                  </div>
                  <p className="text-xs font-bold text-card-foreground">
                    Reading & M4 Central
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Strategic South East Depot
                  </p>
                </div>

                {/* South Tag */}
                <div className="flex justify-between items-end z-10 text-xs font-mono text-muted-foreground">
                  <span className="bg-background/80 px-2 py-0.5 rounded border border-border">
                    HAMPSHIRE (M3)
                  </span>
                  <span className="bg-background/80 px-2 py-0.5 rounded border border-border">
                    SURREY (A3)
                  </span>
                </div>
              </div>

              {/* Transit Key Specifications */}
              <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-border text-xs">
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-primary shrink-0" />
                  <span className="text-muted-foreground">
                    Prompt site surveys arranged
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className="size-4 text-primary shrink-0" />
                  <span className="text-muted-foreground">
                    Full pavement license support
                  </span>
                </div>
              </div>
            </div>

            {/* Highlights Grid */}
            <div className="space-y-3">
              {highlightsData.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3 rounded-lg border border-border bg-card p-3.5 shadow-2xs">
                  <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
                    <CheckCircle2 className="size-3.5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold font-default text-card-foreground">
                      {item.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Accreditation Proof Banner */}
            <div className="flex items-center justify-between rounded-lg bg-secondary p-4 text-secondary-foreground">
              <div className="flex items-center gap-3">
                <ShieldCheck className="size-6 text-primary shrink-0" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-primary">
                    Safety & Compliance
                  </p>
                  <p className="text-xs text-secondary-foreground/90">
                    NASC and CHAS registered
                  </p>
                </div>
              </div>
              <Button asChild size="sm" variant="default" className="text-xs font-semibold">
                <Link to="/HealthSafetyAccreditationsPage">
                  <span>Audits</span>
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}